import type { ReactNode } from "react"
import LicensePackageBadges from "@/v2-backoffice-client/license/changes/LicensePackageBadges"
import {
  contentKindOf,
  contentLockSentenceOf,
  contentPackagesOf,
  isLockedContent,
  type LicenseContentItem,
} from "@/v2-backoffice-client/license/changes/license.content.utils"

export type LicenseContentDetailProps = {
  item: LicenseContentItem
}

type Fact = { label: string; value: ReactNode }

// The content panel's Details tab: what the item is in the words its own
// text uses, which packages carry it and what that means today, what a
// lock does in the map, its categories, and where it is made (the Layers
// and Base maps boards; nothing is added, renamed or published here).
function LicenseContentDetail(props: LicenseContentDetailProps) {
  const { item } = props
  const packages = contentPackagesOf(item)
  const isLocked = isLockedContent(item)
  const kind = contentKindOf(item).toLowerCase()
  const facts: Fact[] = [
    {
      label: isLocked ? "Included in" : "Placed in",
      value: (
        <span className="flex flex-col gap-1">
          <LicensePackageBadges
            names={packages}
            isNested
            variant={isLocked ? "neutral" : "outline"}
            emptyLabel="No package yet"
            isEmptyCritical={isLocked}
          />
          <span className="text-muted-foreground">
            {isLocked
              ? "Customers get it through any one of these, by a pick or by a rule (a whole class, a layer category)."
              : packages.length > 0
                ? "Changes nothing while it is free: these are the packages that keep it the day it is made package-only."
                : "Package-only will ask which packages should carry it; Free for everyone needs none."}
          </span>
        </span>
      ),
    },
    {
      label: isLocked ? "The lock" : "If package-only",
      value: contentLockSentenceOf(item),
    },
    ...(item.CategoryCodes
      ? [{ label: "Categories", value: item.CategoryCodes }]
      : []),
    {
      label: "Made on",
      value: `The ${kind === "base map" ? "Base maps" : "Layers"} board: the item appears here once it has a catalog row, and reaches users once the review queue has made it live and it has a country. Nothing is added, renamed or published from this board.`,
    },
  ]

  return (
    <div className="flex flex-col gap-3 text-xs">
      <p className="text-sm text-muted-foreground">
        {item.Description ??
          `No description has been written for this ${kind} yet.`}
      </p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
        {facts.map((fact) => (
          <div key={fact.label} className="contents">
            <dt className="text-muted-foreground">{fact.label}</dt>
            <dd className="min-w-0">{fact.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

export default LicenseContentDetail
